import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiError, listEditions, listTenantAiSettings, updateTenantAiSettings } from "../services/AiService";
import { AcesEdition, EffectiveAiSettings } from "../types/ai";
import { ErrorAlert, Loading, PageShell } from "../component/aces/Shared";

/** SUPER_ADMIN: per-company QualiCheck AI switch, pinned standard, monthly token budget and image cap. */
const AiSettingsScreen = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<{ tenantId: number; company: string | null; email: string; settings: EffectiveAiSettings; tokensUsedThisMonth: number }[] | null>(null);
  const [editions, setEditions] = useState<AcesEdition[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = () => listTenantAiSettings().then(setRows).catch((e) => { setError(apiError(e)); setRows([]); });
  useEffect(() => {
    load();
    listEditions().then(setEditions).catch(() => setEditions([]));
  }, []);

  const update = (tenantId: number, body: Parameters<typeof updateTenantAiSettings>[1]) =>
    updateTenantAiSettings(tenantId, body).then(load).catch((e) => setError(apiError(e)));

  return (
    <PageShell title={t("aces.settings.title")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      {rows === null ? <Loading /> : (
        <div className="__defaultTable">
          <table>
            <thead>
              <tr><th>{t("aces.settings.company")}</th><th>{t("aces.settings.enabled")}</th><th>{t("aces.settings.edition")}</th><th>{t("aces.settings.budget")}</th><th>{t("aces.settings.used")}</th><th>{t("aces.settings.images")}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tenantId}>
                  <td>{r.company || "—"}<div className="aces-muted">{r.email}</div></td>
                  <td><input type="checkbox" className="form-check-input" checked={r.settings.aiEnabled} onChange={(e) => update(r.tenantId, { aiEnabled: e.target.checked })} /></td>
                  <td>
                    <select className="form-control form-control-sm" style={{ minWidth: 220 }} value={r.settings.pinnedAcesEditionId ?? ""}
                      onChange={(e) => update(r.tenantId, e.target.value ? { acesEditionId: Number(e.target.value) } : { clearAcesEdition: true })}>
                      <option value="">{t("aces.settings.platformDefault")}</option>
                      {editions.map((ed) => <option key={ed.id} value={ed.id}>{ed.code}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" style={{ maxWidth: 160 }} defaultValue={r.settings.monthlyTokenBudget}
                      onBlur={(e) => Number(e.target.value) !== r.settings.monthlyTokenBudget && update(r.tenantId, { monthlyTokenBudget: Number(e.target.value) })} />
                  </td>
                  <td>
                    {r.tokensUsedThisMonth.toLocaleString()}
                    <div className="aces-muted">{r.settings.monthlyTokenBudget > 0 ? `${Math.round((r.tokensUsedThisMonth / r.settings.monthlyTokenBudget) * 100)}%` : "—"}</div>
                  </td>
                  <td>
                    <input type="number" min={0} max={100} className="form-control form-control-sm" style={{ maxWidth: 90 }} defaultValue={r.settings.maxImagesPerAnalysis}
                      onBlur={(e) => Number(e.target.value) !== r.settings.maxImagesPerAnalysis && update(r.tenantId, { maxImagesPerAnalysis: Number(e.target.value) })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
};

export default AiSettingsScreen;
